// 평가단 목록의 '담당 웹사이트/앱' 표시.
import { useRef, useState } from 'react';
// 배정은 사업 단위로 이뤄지므로 셀에는 사업명과 배정 개수만 요약하고, 펼치면 사업별 사이트 목록을 보여준다.
const STATUSES = {
  0: { key: 'todo', label: '미진행' },
  1: { key: 'started', label: '진행중' },
  2: { key: 'done', label: '완료' },
};
const statusOf = (site) => STATUSES[site.complete] ?? STATUSES[0];

export const groupSitesByProgram = (sites = []) => {
  const groups = new Map();
  for (const site of sites) {
    if (!groups.has(site.program_id)) {
      groups.set(site.program_id, {
        program_id: site.program_id,
        program_name: site.program_name ?? '사업 미지정',
        total: site.program_site_count ?? null,
        sites: [],
      });
    }
    groups.get(site.program_id).sites.push(site);
  }
  return [...groups.values()];
};

const countLabel = ({ sites, total }) => {
  if (total === null) return `${sites.length}개`;
  return sites.length === total ? `전체 ${total}개` : `${sites.length}/${total}개`;
};

export const AssignedSitesSummary = ({ sites = [], expanded = false, onToggle }) => {
  if (sites.length === 0) return <span className="assigned-sites-empty">배정 없음</span>;

  const groups = groupSitesByProgram(sites);
  return (
    <div className="assigned-sites">
      <span className="assigned-sites-program" title={groups.map(g => g.program_name).join(', ')}>
        {groups[0].program_name}
      </span>
      {groups.length > 1 && <span className="assigned-sites-extra">외 {groups.length - 1}</span>}
      <button
        type="button"
        className={`assigned-sites-toggle${expanded ? ' is-open' : ''}`}
        aria-expanded={expanded}
        onClick={onToggle}
      >
        {groups.length > 1 ? `${sites.length}개` : countLabel(groups[0])}
        <span aria-hidden="true">{expanded ? '▴' : '▾'}</span>
      </button>
    </div>
  );
};

export const AssignedSitesDetail = ({ sites = [], userName = '사용자', onRemove }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const pending = useRef(false);
  const selected = sites.filter(site => selectedIds.includes(site.assignment_id));
  const selectableIds = sites.filter(site => site.assignment_id).map(site => site.assignment_id);
  const removeSelected = async () => {
    if (pending.current || selected.length === 0) return;
    if (!window.confirm(`${userName}님의 다음 ${selected.length}개 배정을 해제하시겠습니까?\n\n${selected.map(s => s.site_name).join('\n')}\n\n기존 응답은 보존됩니다.`)) return;
    pending.current = true;
    setRemoving(true);
    setError('');
    setNotice('');
    try {
      await onRemove(selected.map(site => site.assignment_id));
      setSelectedIds([]);
      setNotice(`${selected.length}개 배정을 해제했습니다.`);
    } catch (err) {
      setError(err.message || '배정 해제에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      pending.current = false;
      setRemoving(false);
    }
  };
  return (
  <div className="assigned-sites-detail">
    {onRemove && <div className="assignment-remove-toolbar">
      <span>해제할 웹사이트·앱을 선택하세요. 기존 응답은 보존됩니다.</span>
      <div className="assignment-selection-actions">
      <button type="button" className="assignment-select-button" disabled={removing || selected.length === selectableIds.length} onClick={() => { setSelectedIds(selectableIds); setNotice(''); }}>전체 선택</button>
      <button type="button" className="assignment-select-button" disabled={removing || selected.length === 0} onClick={() => { setSelectedIds([]); setNotice(''); }}>전체 선택 해제</button>
      <button type="button" className="assignment-remove-button" disabled={removing || selected.length === 0} onClick={removeSelected}>
        {removing ? '해제 중…' : `선택 배정 해제 (${selected.length})`}
      </button>
      </div>
    </div>}
    {error && <p role="alert" className="assignment-remove-error">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {groupSitesByProgram(sites).map(group => {
      const countByStatus = key => group.sites.filter(s => statusOf(s).key === key).length;
      return (
        <section key={group.program_id ?? 'none'} className="assigned-sites-group">
          <div className="assigned-sites-group-header">
            <strong>{group.program_name}</strong>
            <span className="assigned-sites-group-count">{countLabel(group)}</span>
            <span className="assigned-sites-legend">
              {Object.values(STATUSES).reverse().map(({ key, label }) => (
                <span key={key}>
                  <i className={`assigned-site-dot is-${key}`} aria-hidden="true" />
                  {label} {countByStatus(key)}
                </span>
              ))}
            </span>
          </div>
          <ul className="assigned-sites-grid">
            {group.sites.map(site => {
              const status = statusOf(site);
              const ItemContent = onRemove ? 'label' : 'div';
              return (
                <li key={site.site_id} className={`assigned-site is-${status.key}`} title={`${site.site_name} (${status.label})`}>
                  <ItemContent className={`assigned-site-content${onRemove ? ' is-selectable' : ''}`}>
                  {onRemove && <input type="checkbox" className="assignment-remove-checkbox" aria-label={`${site.site_name} 배정 해제 선택`}
                    checked={selectedIds.includes(site.assignment_id)} disabled={removing || !site.assignment_id}
                    onChange={e => {
                      setNotice('');
                      setSelectedIds(prev => e.target.checked ? [...prev, site.assignment_id] : prev.filter(id => id !== site.assignment_id));
                    }} />}
                  <i className={`assigned-site-dot is-${status.key}`} aria-hidden="true" />
                  <span className="assigned-site-name">{site.site_name}</span>
                  <span className="assigned-sites-sr">{status.label}</span>
                  </ItemContent>
                </li>
              );
            })}
          </ul>
        </section>
      );
    })}
  </div>
  );
};
